/* TaxExemptionsDialog — Infosys Procurement Tax Team × Customer Legal/Tax
   Certificate management UI. Lists active + revoked certificates for the
   current buyer (network_id + company_name) and lets the user upload new
   ones (PDF / image up to 12 MB).

   When a matching active certificate exists for the buyer's country (+
   optional state / UNSPSC segment), the catalog and cart auto-apply a 0%
   tax rate with the "Exempt — certificate on file" treatment driven by
   the tax engine (`/app/backend/tax.py`). No UI lookup happens here.
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  ShieldCheck,
  Upload,
  FileText,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  Sparkles,
  ArrowLeft,
  Plus,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import Flag from "../Flag";
import { useLocale } from "../../i18n/LocaleContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRY_OPTIONS = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  { code: "NL", label: "Netherlands" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "IT", label: "Italy" },
  { code: "ES", label: "Spain" },
  { code: "BE", label: "Belgium" },
  { code: "PT", label: "Portugal" },
  { code: "CH", label: "Switzerland" },
  { code: "BR", label: "Brazil" },
  { code: "MX", label: "Mexico" },
  { code: "AR", label: "Argentina" },
  { code: "IN", label: "India" },
  { code: "AU", label: "Australia" },
  { code: "NZ", label: "New Zealand" },
  { code: "CN", label: "China" },
  { code: "JP", label: "Japan" },
  { code: "SG", label: "Singapore" },
  { code: "AE", label: "UAE" },
  { code: "SA", label: "Saudi Arabia" },
];

const UNSPSC_SEGMENT_OPTIONS = [
  { code: "31", label: "Industrial mfg / supply (31)" },
  { code: "39", label: "Electrical (39)" },
  { code: "40", label: "Pneumatic / hydraulic (40)" },
  { code: "41", label: "Lab equipment (41)" },
  { code: "42", label: "Medical (42)" },
  { code: "46", label: "PPE / Safety (46)" },
  { code: "47", label: "Cleaning / Sanitation (47)" },
  { code: "48", label: "Food handling (48)" },
  { code: "50", label: "Food & beverage (50)" },
  { code: "51", label: "Pharma / drugs (51)" },
  { code: "55", label: "Stationery / office (55)" },
];

function fmtBytes(b) {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

export default function TaxExemptionsDialog({ open, onOpenChange, session, onChanged }) {
  const { t } = useLocale();
  const [items, setItems] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("list"); // "list" | "upload"

  // Upload form state
  const [country, setCountry] = useState(session?.country_code || "NL");
  const [state, setState] = useState("");
  const [reason, setReason] = useState("Non-Profit (501(c)(3) or equiv.)");
  const [certNumber, setCertNumber] = useState("");
  const [issuedBy, setIssuedBy] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [unspscSegments, setUnspscSegments] = useState([]); // string[]
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const buyerKey = useMemo(
    () => (session ? `${session.network_id}|${session.company_name}` : null),
    [session?.network_id, session?.company_name]
  );

  const load = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/infoshop/tax/exemptions`, {
          params: { network_id: session.network_id, company_name: session.company_name },
        }),
        axios.get(`${API}/infoshop/tax/exemption-reasons`),
      ]);
      setItems(a.data || []);
      setReasons(b.data?.reasons || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load exemption certificates");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (open) {
      load();
      setMode("list");
    }
     
  }, [open, buyerKey]);

  const resetForm = () => {
    setCountry(session?.country_code || "NL");
    setState("");
    setReason(reasons[0] || "Non-Profit (501(c)(3) or equiv.)");
    setCertNumber("");
    setIssuedBy("");
    setValidFrom("");
    setValidTo("");
    setUnspscSegments([]);
    setNotes("");
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const toggleSeg = (code) => {
    setUnspscSegments((prev) =>
      prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]
    );
  };

  const submitUpload = async () => {
    if (!session) return;
    if (!country) return toast.error("Country is required");
    if (!reason) return toast.error("Exemption reason is required");
    if (!certNumber.trim()) return toast.error("Certificate number is required");
    if (!file) return toast.error("Please attach the certificate file (PDF/image)");

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("network_id", session.network_id);
      fd.append("company_name", session.company_name);
      fd.append("country_code", country);
      if (state.trim()) fd.append("state", state.trim());
      fd.append("reason", reason);
      fd.append("certificate_number", certNumber.trim());
      if (issuedBy.trim()) fd.append("issued_by", issuedBy.trim());
      if (validFrom) fd.append("valid_from", validFrom);
      if (validTo) fd.append("valid_to", validTo);
      if (unspscSegments.length) fd.append("unspsc_segments", unspscSegments.join(","));
      if (notes.trim()) fd.append("notes", notes.trim());
      fd.append("uploaded_by", session.display_name || session.network_id);
      fd.append("file", file);

      const { data } = await axios.post(`${API}/infoshop/tax/exemptions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Certificate ${data.id} saved · ${data.country_code}`);
      await load();
      onChanged?.();
      resetForm();
      setMode("list");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (ex) => {
    if (!window.confirm(`Revoke certificate ${ex.id}?\n\nFuture orders for ${ex.country_code} will be taxed at the standard rate.`)) return;
    try {
      await axios.delete(`${API}/infoshop/tax/exemptions/${ex.id}`);
      toast.success(`Certificate ${ex.id} revoked`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const active = items.filter((x) => x.status === "active");
  const inactive = items.filter((x) => x.status !== "active");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="tax-exemptions-dialog"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-emerald-300/40 bg-emerald-50 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">
                <ShieldCheck size={11} /> {t("exempt.dialog.eyebrow")}
              </div>
              <DialogTitle className="mt-2 font-display text-2xl tracking-tight">
                {t("exempt.dialog.title")}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-slate-600 leading-relaxed max-w-2xl">
                {t("exempt.dialog.subtitle")}
              </DialogDescription>
            </div>
            <div className="text-right hidden md:block shrink-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold">
                {t("exempt.dialog.for_buyer")}
              </div>
              <div className="font-mono text-sm font-semibold text-slate-900 mt-1">
                {session?.network_id || "—"}
              </div>
              <div className="text-[11px] text-slate-500">{session?.company_name}</div>
            </div>
          </div>
        </DialogHeader>

        {mode === "list" && (
          <div className="mt-2 space-y-5">
            {/* Quick-stats strip */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="exempt-stat-active">
                <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-bold">{t("exempt.dialog.stat_active")}</div>
                <div className="font-display text-2xl text-emerald-800 font-bold mt-0.5">{active.length}</div>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="exempt-stat-coverage">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{t("exempt.dialog.stat_coverage")}</div>
                <div className="font-display text-sm text-slate-800 font-bold mt-1 leading-tight">
                  {new Set(active.map((x) => x.country_code)).size} {t("exempt.dialog.countries")}
                </div>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="exempt-stat-pending-rev">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{t("exempt.dialog.stat_revoked")}</div>
                <div className="font-display text-2xl text-slate-700 font-bold mt-0.5">{inactive.length}</div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-500">
                {t("exempt.dialog.list_title")}
              </div>
              <Button
                onClick={() => { resetForm(); setMode("upload"); }}
                data-testid="exempt-upload-cta"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Plus size={14} className="mr-1" /> {t("exempt.dialog.upload_cta")}
              </Button>
            </div>

            {loading && (
              <div className="py-10 text-center text-sm text-slate-500 inline-flex items-center justify-center gap-2 w-full">
                <Loader2 className="animate-spin" size={16} /> {t("exempt.dialog.loading")}
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center" data-testid="exempt-empty">
                <ShieldCheck size={28} className="mx-auto text-slate-400" />
                <div className="mt-3 font-display text-base font-semibold text-slate-900">
                  {t("exempt.dialog.empty_title")}
                </div>
                <div className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
                  {t("exempt.dialog.empty_help")}
                </div>
                <Button
                  onClick={() => { resetForm(); setMode("upload"); }}
                  data-testid="exempt-upload-cta-empty"
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Upload size={14} className="mr-1.5" /> {t("exempt.dialog.upload_first")}
                </Button>
              </div>
            )}

            {!loading && items.length > 0 && (
              <div className="space-y-2">
                {items.map((ex) => (
                  <div
                    key={ex.id}
                    data-testid={`exempt-row-${ex.id}`}
                    className={`rounded-lg border p-3 ${
                      ex.status === "active"
                        ? "bg-white border-slate-200 hover:border-emerald-300"
                        : "bg-slate-50 border-slate-200 opacity-70"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 mt-0.5">
                        {ex.status === "active" ? (
                          <CheckCircle2 size={18} className="text-emerald-600" />
                        ) : (
                          <XCircle size={18} className="text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Flag code={ex.country_code} height={14} />
                          <span className="font-mono text-sm font-semibold text-slate-900">{ex.id}</span>
                          {ex.state && (
                            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                              {ex.state}
                            </span>
                          )}
                          <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                            {ex.reason}
                          </span>
                          {ex.unspsc_segments?.length > 0 && (
                            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">
                              {t("exempt.dialog.segments_short")}: {ex.unspsc_segments.join(", ")}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm text-slate-700">
                          <span className="font-semibold">{t("exempt.dialog.cert_no")}:</span>{" "}
                          <span className="font-mono">{ex.certificate_number}</span>
                          {ex.issued_by ? <> · <span className="text-slate-600">{ex.issued_by}</span></> : null}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                          {ex.valid_from && <span>{t("exempt.dialog.valid_from")}: {ex.valid_from}</span>}
                          {ex.valid_to && <span>{t("exempt.dialog.valid_to")}: {ex.valid_to}</span>}
                          <span>· {fmtBytes(ex.file_size)} · {ex.file_mime}</span>
                          <span>· {t("exempt.dialog.uploaded")} {ex.uploaded_at?.slice(0, 10)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a
                          href={`${API}/infoshop/tax/exemptions/${ex.id}/file`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`exempt-download-${ex.id}`}
                          title={t("exempt.dialog.view_file")}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                        >
                          <ExternalLink size={14} />
                        </a>
                        {ex.status === "active" && (
                          <button
                            onClick={() => revoke(ex)}
                            data-testid={`exempt-revoke-${ex.id}`}
                            title={t("exempt.dialog.revoke")}
                            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-rose-600 hover:bg-rose-50"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-[12px] text-emerald-900 inline-flex items-start gap-2 leading-relaxed">
              <Sparkles size={14} className="text-emerald-700 mt-0.5 shrink-0" />
              <span>
                {t("exempt.dialog.governance_footer")}
              </span>
            </div>
          </div>
        )}

        {mode === "upload" && (
          <div className="mt-2 space-y-4">
            <button
              onClick={() => setMode("list")}
              data-testid="exempt-back-to-list"
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft size={12} /> {t("exempt.dialog.back_to_list")}
            </button>

            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">{t("exempt.upload.title")}</h3>
              <p className="text-xs text-slate-600 mt-1">{t("exempt.upload.help")}</p>
            </div>

            {/* File dropzone */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5">
                {t("exempt.upload.file_label")}
              </label>
              <label
                htmlFor="exempt-file-input"
                data-testid="exempt-upload-dropzone"
                className={`block rounded-xl border-2 border-dashed p-5 text-center cursor-pointer transition-colors ${
                  file ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/40"
                }`}
              >
                <input
                  ref={fileRef}
                  id="exempt-file-input"
                  data-testid="exempt-file-input"
                  type="file"
                  className="hidden"
                  accept="application/pdf,image/png,image/jpeg,image/heic"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                {file ? (
                  <div className="inline-flex items-center gap-2">
                    <FileText size={20} className="text-emerald-600" />
                    <div className="text-left">
                      <div className="font-semibold text-slate-900 text-sm">{file.name}</div>
                      <div className="text-[11px] text-slate-500">{fmtBytes(file.size)} · {file.type || "file"}</div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload size={20} className="mx-auto text-slate-400" />
                    <div className="text-sm font-semibold text-slate-700 mt-1.5">{t("exempt.upload.dropzone")}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">PDF · PNG · JPG · HEIC · up to 12 MB</div>
                  </div>
                )}
              </label>
            </div>

            {/* Form grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">{t("exempt.upload.country")}</label>
                <select
                  data-testid="exempt-form-country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} · {c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">
                  {t("exempt.upload.state")} <span className="text-slate-400 font-normal">({t("exempt.upload.optional")})</span>
                </label>
                <input
                  data-testid="exempt-form-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="e.g. CA, NY, SP, RJ"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono uppercase focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">{t("exempt.upload.reason")}</label>
                <select
                  data-testid="exempt-form-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                >
                  {reasons.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">{t("exempt.upload.cert_no")}</label>
                <input
                  data-testid="exempt-form-cert-number"
                  value={certNumber}
                  onChange={(e) => setCertNumber(e.target.value)}
                  placeholder="e.g. EX-2026-US-44215"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">
                  {t("exempt.upload.issued_by")} <span className="text-slate-400 font-normal">({t("exempt.upload.optional")})</span>
                </label>
                <input
                  data-testid="exempt-form-issued-by"
                  value={issuedBy}
                  onChange={(e) => setIssuedBy(e.target.value)}
                  placeholder="e.g. IRS · Receita Federal · HMRC"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">
                  {t("exempt.upload.valid_from")} <span className="text-slate-400 font-normal">({t("exempt.upload.optional")})</span>
                </label>
                <input
                  data-testid="exempt-form-valid-from"
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">
                  {t("exempt.upload.valid_to")} <span className="text-slate-400 font-normal">({t("exempt.upload.optional")})</span>
                </label>
                <input
                  data-testid="exempt-form-valid-to"
                  type="date"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">
                  {t("exempt.upload.unspsc_label")} <span className="text-slate-400 font-normal">({t("exempt.upload.unspsc_help")})</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {UNSPSC_SEGMENT_OPTIONS.map((s) => {
                    const selected = unspscSegments.includes(s.code);
                    return (
                      <button
                        key={s.code}
                        type="button"
                        onClick={() => toggleSeg(s.code)}
                        data-testid={`exempt-form-unspsc-${s.code}`}
                        className={`text-[11px] px-2.5 py-1.5 rounded-full border transition-colors ${
                          selected
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">
                  {t("exempt.upload.notes")} <span className="text-slate-400 font-normal">({t("exempt.upload.optional")})</span>
                </label>
                <textarea
                  data-testid="exempt-form-notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("exempt.upload.notes_placeholder")}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-3">
              <Button variant="ghost" onClick={() => setMode("list")} className="text-slate-600">
                <ArrowLeft size={14} className="mr-1" /> {t("exempt.dialog.back_to_list")}
              </Button>
              <Button
                onClick={submitUpload}
                disabled={submitting || !file || !certNumber.trim() || !reason || !country}
                data-testid="exempt-form-submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 font-semibold"
              >
                {submitting ? (
                  <><Loader2 className="animate-spin mr-1.5" size={14} /> {t("exempt.upload.submitting")}</>
                ) : (
                  <><ShieldCheck size={14} className="mr-1.5" /> {t("exempt.upload.submit")}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
